import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  CheckCircle, 
  ArrowRight,
  Smile,
  Heart,
  Zap,
  Shield
} from 'lucide-react';
import SalesChatWidget from './SalesChatWidget';
import { rememberPendingPlan } from '../services/checkout';

const pricingPlans = [
  {
    name: "Per User",
    price: "$19",
    unit: "per user / month",
    subtitle: "Scale user by user",
    annualPrice: "$16.15/user/mo billed annually",
    annualSavings: "Save 15% · $193.80/user/year",
    priceId: "per_user_monthly",
    annualPriceId: "per_user_annual",
    features: [
      "✓ Price locked for life",
      "AI-powered ticket handling",
      "Unlimited tickets",
      "Email & chat support",
      "Analytics & reporting",
      "Integrates with existing systems",
      "Month-to-month · no contracts"
    ],
    cta: "Start Now",
    popular: false
  },
  {
    name: "Lifetime",
    price: "$229",
    unit: "one-time · per user",
    subtitle: "Never receive a bill from us again",
    annualPrice: "Pay once. Done.",
    annualSavings: "No subscriptions · no renewals · forever",
    priceId: "lifetime",
    oneTime: true,
    features: [
      "✓ Lifetime access · never billed again",
      "Everything in Per User",
      "All future updates included",
      "Pays for itself in ~12 months",
      "Transfer between team members",
      "No expiration · no fine print",
      "The anti-subscription"
    ],
    cta: "Get Lifetime",
    popular: true,
    badge: "Pay once. Done."
  },
  {
    name: "Unlimited",
    price: "$299",
    unit: "per month · unlimited users",
    subtitle: "One flat rate. Add anyone.",
    annualPrice: "$254.15/mo billed annually",
    annualSavings: "Save 15% · $3,049.80/year",
    priceId: "unlimited_monthly",
    annualPriceId: "unlimited_annual",
    features: [
      "✓ Price locked for life",
      "Everything in Per User",
      "Unlimited users",
      "T2 AI — 3x faster response",
      "Priority support",
      "Advanced analytics & API access",
      "Month-to-month · no contracts"
    ],
    cta: "Start Now",
    popular: false
  }
];

const LandingPage = ({ onNavigate }) => {
  const [audioPlayed, setAudioPlayed] = useState(false);

  useEffect(() => {
    if (audioPlayed) return;

    const playOnce = () => {
      if (audioPlayed) return;
      const audio = new Audio('/dont-worry-be-happy.mp3');
      audio.volume = 0.25;
      audio.play().catch(() => {
        // If file is missing or browser still blocks, fail silently
      });
      // Stop after 30s
      setTimeout(() => {
        try { audio.pause(); } catch (e) { /* ignore */ }
      }, 30000);
      setAudioPlayed(true);
      window.removeEventListener('click', playOnce);
      window.removeEventListener('keydown', playOnce);
      window.removeEventListener('touchstart', playOnce);
    };

    window.addEventListener('click', playOnce);
    window.addEventListener('keydown', playOnce);
    window.addEventListener('touchstart', playOnce);

    return () => {
      window.removeEventListener('click', playOnce);
      window.removeEventListener('keydown', playOnce);
      window.removeEventListener('touchstart', playOnce);
    };
  }, [audioPlayed]);

  const features = [
    {
      title: "No Contracts",
      description: "Month to month. Cancel anytime. That's it."
    },
    {
      title: "Price Locked Forever",
      description: "Your rate today stays your rate. Forever. We promise."
    },
    {
      title: "Pay When Ready",
      description: "You tell us when. No reminders. No late fees. Ever."
    },
    {
      title: "We Trust You",
      description: "Can't pay a month? We get it. Life happens. Just pick up next month."
    },
    {
      title: "80% Auto-Resolved",
      description: "Our AI handles most tickets. 4.2 minute average."
    },
    {
      title: "Works With Your Systems",
      description: "No migration. No replacement. Just support."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Simple Nav */}
      <nav className="border-b border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Simplified Helpdesk Logo - stacked bars getting shorter */}
              <div className="relative w-12 h-12">
                <svg viewBox="0 0 48 48" className="w-12 h-12">
                  <rect x="8"  y="11" width="32" height="6" rx="2" fill="#111827" />
                  <rect x="8"  y="21" width="22" height="6" rx="2" fill="#374151" />
                  <rect x="8"  y="31" width="12" height="6" rx="2" fill="#FCD34D" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Simplified Helpdesk</h1>
                <p className="text-sm text-gray-500">Automation-first IT support</p>
              </div>
            </div>
            <div className="flex items-center gap-8">
              <a href="/sales.html" className="text-gray-600 hover:text-gray-900 text-sm">Pitch</a>
              <a href="/tutorial.html" className="text-gray-600 hover:text-gray-900 text-sm">Product tour</a>
              <a href="#features" className="text-gray-600 hover:text-gray-900">How it works</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900">Pricing</a>
              <button
                data-testid="nav-signin-btn"
                onClick={() => onNavigate('login')}
                className="text-gray-600 hover:text-gray-900 text-sm"
              >
                Sign in
              </button>
              <Button
                data-testid="nav-getstarted-btn"
                onClick={() => onNavigate('signup')}
                className="bg-gray-900 hover:bg-gray-800 text-white"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero - Ultra Simple */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-6xl font-bold text-gray-900 mb-8 leading-tight">
            IT support without
            <br />
            the hassle
          </h2>
          <p className="text-2xl text-gray-600 mb-12 leading-relaxed">
            No contracts. No late fees. No collections. No pressure.
            <br />
            Just AI-powered ticket support. Pay when you're ready.
          </p>
          <Button 
            size="lg"
            onClick={() => onNavigate('signup')}
            className="bg-gray-900 hover:bg-gray-800 text-white text-lg px-10 py-7"
          >
            Start now <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-sm text-gray-500 mt-6">
            Try risk-free • Annual plans save 15%
          </p>
        </div>
      </section>

      {/* What is it - Product Explanation */}
      <section className="py-20 px-6 border-t border-gray-200">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">What is Simplified Helpdesk?</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              An autonomous helpdesk platform that answers tickets instantly, resolves common issues automatically, and operates 24/7.
              <br />
              It integrates into your existing systems — no replacement needed.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 mb-12">
            <div className="border border-gray-200 rounded-lg p-8">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">What it does</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-gray-400">→</span>
                  <span>Receives tickets from email, chat, or portal</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gray-400">→</span>
                  <span>AI analyzes the request and finds solutions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gray-400">→</span>
                  <span>Automatically resolves 80% of common issues</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gray-400">→</span>
                  <span>Routes complex tickets to your team with AI suggestions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gray-400">→</span>
                  <span>Learns from every interaction to improve accuracy</span>
                </li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg p-8">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Common tickets it handles</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>Password resets & account unlocks</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>VPN & network connection issues</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>Software installation requests</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>Email sync & setup problems</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>Printer & device troubleshooting</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>Access permission requests</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-8 border border-gray-200">
            <h4 className="text-xl font-semibold text-gray-900 mb-4">How it integrates with your systems</h4>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="font-medium text-gray-900 mb-2">Receives tickets via:</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Email forwarding</li>
                  <li>• Microsoft Teams</li>
                  <li>• Slack integration</li>
                  <li>• Web portal</li>
                  <li>• API connections</li>
                </ul>
              </div>
              <div>
                <div className="font-medium text-gray-900 mb-2">Works with:</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Your existing helpdesk</li>
                  <li>• Active Directory / SSO</li>
                  <li>• Your knowledge base</li>
                  <li>• Current ticketing system</li>
                  <li>• IT management tools</li>
                </ul>
              </div>
              <div>
                <div className="font-medium text-gray-900 mb-2">You get:</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Real-time dashboard</li>
                  <li>• Ticket analytics</li>
                  <li>• AI performance metrics</li>
                  <li>• Custom reporting</li>
                  <li>• Full audit trails</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-lg text-gray-700 mb-4">
              <strong>Think of it as:</strong> An extra IT support person that handles the repetitive stuff, 
              so your team can focus on complex problems.
            </p>
            <p className="text-gray-600">
              No migration. No replacement. Just plug it in and let it help.
            </p>
          </div>
        </div>
      </section>

      {/* Easy Setup - No Hassle Installation */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Setup is actually easy</h3>
            <p className="text-xl text-gray-600">
              5 minutes. That's it. No integration fees. No hassle.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-8">
            <Card className="border-2 border-gray-200 p-8">
              <h4 className="text-xl font-semibold text-gray-900 mb-6">What we DON'T do</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="text-2xl text-gray-300">✗</span>
                  <div>
                    <div className="font-medium text-gray-900">No integration fees</div>
                    <div className="text-sm text-gray-600">Setup is included. Period.</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl text-gray-300">✗</span>
                  <div>
                    <div className="font-medium text-gray-900">No 2-hour windows</div>
                    <div className="text-sm text-gray-600">We're not a cable company. You pick the exact time.</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl text-gray-300">✗</span>
                  <div>
                    <div className="font-medium text-gray-900">No account login headaches</div>
                    <div className="text-sm text-gray-600">Forget passwords? Not here. Simple access.</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl text-gray-300">✗</span>
                  <div>
                    <div className="font-medium text-gray-900">No business hours only</div>
                    <div className="text-sm text-gray-600">Need setup at 2am on Sunday? We'll do it.</div>
                  </div>
                </li>
              </ul>
            </Card>

            <Card className="border-2 border-gray-900 p-8 bg-gray-50">
              <h4 className="text-xl font-semibold text-gray-900 mb-6">What we DO</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900">5-minute installation</div>
                    <div className="text-sm text-gray-600">Connect your email or Slack. That's literally it.</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900">You choose the exact time</div>
                    <div className="text-sm text-gray-600">Tell us when. We'll be there. Any day. Any time. Even 3am.</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900">No password nonsense</div>
                    <div className="text-sm text-gray-600">Simple, secure access. No "forgot password" emails.</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900">Zero integration fees</div>
                    <div className="text-sm text-gray-600">Setup is free. Always. No hidden charges.</div>
                  </div>
                </li>
              </ul>
            </Card>
          </div>

          <div className="bg-white rounded-lg border-2 border-gray-900 p-8">
            <div className="flex items-start gap-6">
              <div className="bg-gray-900 text-white rounded-full w-16 h-16 flex items-center justify-center flex-shrink-0 text-2xl font-bold">
                5m
              </div>
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3">
                  Seriously. 5 minutes from start to handling tickets.
                </h4>
                <p className="text-gray-700 text-lg mb-4">
                  You tell us when you want setup. Could be Tuesday at 2:47pm. Could be Friday at midnight. 
                  We don't care. We'll be there at that exact time.
                </p>
                <p className="text-gray-600">
                  No waiting around. No "sometime between 1-5pm" garbage. No integration team. 
                  No consultants. Just you, 5 minutes, and it's done.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats - Simple */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-4 gap-12 text-center">
            <div>
              <div className="text-5xl font-bold text-gray-900 mb-2">82%</div>
              <div className="text-gray-600">Auto-resolved</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-gray-900 mb-2">4.2min</div>
              <div className="text-gray-600">Avg resolution</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-gray-900 mb-2">$19</div>
              <div className="text-gray-600">Starting price</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-gray-900 mb-2">0</div>
              <div className="text-gray-600">Contracts needed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features - Grid */}
      <section id="features" className="py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-4xl font-bold text-gray-900 mb-16 text-center">
            How it works
          </h3>
          <div className="grid grid-cols-2 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-8 hover:border-gray-300 transition-colors">
                <h4 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h4>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section className="py-32 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-6">Our promise</h3>
            <p className="text-xl text-gray-600">
              We're so confident you'll love it, we're removing every bit of friction.
            </p>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-4 bg-white p-6 rounded-lg border border-gray-200">
              <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-gray-900 mb-1">Price locked for life</div>
                <div className="text-gray-600">The price you see today is your price forever. No surprises.</div>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-lg border border-gray-200">
              <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-gray-900 mb-1">Pay when you're ready</div>
                <div className="text-gray-600">You tell us when to charge you. No automated billing reminders.</div>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-lg border border-gray-200">
              <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-gray-900 mb-1">No late fees, ever</div>
                <div className="text-gray-600">Can't pay this month? Skip it. We're not that kind of company.</div>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-lg border border-gray-200">
              <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-gray-900 mb-1">Cancel anytime</div>
                <div className="text-gray-600">Month to month. No contracts. No questions asked.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing - Clean */}
      <section id="pricing" className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Simple pricing</h3>
            <p className="text-xl text-gray-600">Three plans. Price locked for life. Or pay once and never see a bill again.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, idx) => (
              <div 
                key={idx}
                className={`border-2 rounded-lg p-8 ${
                  plan.popular 
                    ? 'border-gray-900 shadow-lg' 
                    : 'border-gray-200'
                }`}
              >
                {plan.popular && (
                  <Badge className="mb-4 bg-gray-900 text-white border-0">
                    {plan.badge || 'Most popular'}
                  </Badge>
                )}
                <div className="mb-6">
                  <div className="text-sm text-gray-600 mb-2">{plan.name}</div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <div className="text-5xl font-bold text-gray-900">{plan.price}</div>
                    <div className="text-sm text-gray-500">{plan.unit}</div>
                  </div>
                  <div className="text-sm text-gray-500 mb-3">{plan.subtitle}</div>
                  <div className="pt-3 border-t border-gray-100">
                    <div className="text-sm font-medium text-gray-900">{plan.oneTime ? plan.annualPrice : `or ${plan.annualPrice}`}</div>
                    <div className="text-xs text-green-700 mt-0.5">{plan.annualSavings}</div>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-gray-900 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  data-testid={`pricing-cta-${plan.priceId}`}
                  onClick={() => {
                    if (plan.priceId) rememberPendingPlan(plan.priceId, 1);
                    onNavigate('signup');
                  }}
                  className={`w-full ${
                    plan.popular 
                      ? 'bg-gray-900 hover:bg-gray-800 text-white' 
                      : 'bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200'
                  }`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-10">
            Need enterprise SSO, custom AI training, or 99.9% SLA? <a href="#" className="text-gray-900 underline">Contact sales</a>.
          </p>
        </div>
      </section>

      {/* CTA - Simple */}
      <section className="py-32 px-6 bg-gray-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-5xl font-bold mb-6">
            Ready to try it?
          </h3>
          <p className="text-xl text-gray-300 mb-10">
            No contracts. No credit card needed. Just simplified IT support.
            <br />
            Annual plans save 15% — price locked for life.
          </p>
          <Button 
            size="lg"
            onClick={() => onNavigate('signup')}
            className="bg-white hover:bg-gray-100 text-gray-900 text-lg px-10 py-7"
          >
            Get started now
          </Button>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer className="border-t border-gray-200 py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div>© 2026 Simplified Helpdesk</div>
            <div className="flex gap-8">
              <a href="/privacy.html" className="hover:text-gray-900">Privacy</a>
              <a href="/terms.html" className="hover:text-gray-900">Terms</a>
              <a href="mailto:hello@simplifiedhelpdesk.com" className="hover:text-gray-900">Contact</a>
            </div>
          </div>
        </div>
      </footer>

      {/* AI Sales Chatbot (bottom-right) - public landing chatbot */}
      <SalesChatWidget />
    </div>
  );
};

export default LandingPage;
